import type { Settings } from "@go-novus/admin-api";
import { Panel } from "@go-novus/ui";

import type { ProactiveRuleDraft } from "#/features/settings";

import { usePreviewColor } from "../../hooks/use-preview-color";
import type { PreviewControl } from "../../hooks/use-preview-control";
import { selectPromptTemplates } from "../../lib/proactive-preview";
import { ChatboxPreview } from "./chatbox-preview";

/** 草稿里表单字段和预览共同读的那几项：AI 名称、chatbox 外观、当前 Logo 与主动消息规则。 */
export interface DraftView {
  agentName: string;
  chatbox: Settings["chatbox"];
  /** 当前该显示的 Logo：待保存的新图优先，否则是已保存的那张。 */
  logoUrl: string | null;
  /** 草稿里的主动消息规则，预览用它选主动提示的文案和预填问题。 */
  proactiveRules: ProactiveRuleDraft[];
}

export interface PreviewPanelProps {
  draftView: DraftView;
  /** 最后操作过的规则 id；只有 Proactive messages 标签页会传，其他标签按回退规则选。 */
  operatedRuleId?: string;
  preview: PreviewControl;
}

/**
 * 预览栏：一张不自己滚动的 Panel，放不下时由舞台把挂件等比缩小。
 * 宽屏时钉在视口顶部，高度扣掉上下留白、底部保存条和间距，保存条变高（比如报错折行）会被压住一截；
 * 窄屏时排在表单下方、不钉住，固定 26rem 高。
 * 在整行里的占位规则由 PreviewLayout 管；视图与设备是上游受控值。
 */
export function PreviewPanel({
  draftView,
  operatedRuleId,
  preview,
}: PreviewPanelProps) {
  const themeColor = usePreviewColor(draftView.chatbox.themeColor);
  // 规则列表只喂给提示文案与预填问题的选择，不摊进预览组件。
  const { proactiveRules, ...chatboxView } = draftView;
  const templates = selectPromptTemplates(proactiveRules, operatedRuleId);

  return (
    <Panel className="flex min-w-0 flex-col h-[26rem] @min-[60rem]:sticky @min-[60rem]:top-0 @min-[60rem]:h-[calc(100dvh-8.5rem)]">
      <ChatboxPreview
        {...chatboxView}
        {...preview}
        promptTemplate={templates?.promptTemplate}
        questionTemplate={templates?.questionTemplate}
        themeColor={themeColor}
      />
    </Panel>
  );
}
